import { formatDate } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTable } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AddTicketDialogComponent } from '../add-ticket-dialog/add-ticket-dialog.component';
import { DateTimePickerComponent } from '../date-time-picker/date-time-picker.component';
import { ErrorWarningDialogComponent } from '../error-warning-dialog/error-warning-dialog.component';
import { PermitNumDialogComponent } from '../permit-num-dialog/permit-num-dialog.component';
import { SetSelectionDialogComponent } from '../set-selection-dialog/set-selection-dialog.component';
import { CommonFunctionService } from '../shared/common-function-service.service';
import { FishTicket } from '../shared/delivery-fish-ticket.model';
import { FishingEvent } from '../shared/fishing-event.model';
import { GroundfishDelivery } from '../shared/groundfish-delivery.model';
import { LocalStorageService } from '../shared/local-storage.service';
import { LookupDataService } from '../shared/lookup-data.service';
import { LookupItem } from '../shared/lookup-item.model';
import { SetErrorBlock } from '../shared/set-error-block.model';
import { TripValidator, lookupItemValidator, vesselDataValidator } from '../shared/trip-validator.model';
import { SubmitDialogComponent } from '../submit-dialog/submit-dialog.component';
import { ConfirmDialogComponent } from '../vessel-reg/confirm-dialog/confirm-dialog.component';
import { VesselData } from '../vessel-reg/vessel-data.model';
import { Trip } from '../shared/trip.model';
import { Insights } from '../shared/insights.service';


enum DeliveryMode { Current, Completed, EditCompleted};


/**
 * The current delivery page. User will add their sets and finish the delivery here.
 * This component is used to display current delivery and when viewing any completed delivery.
 */
@Component({
    selector: 'app-delivery-detail',
    templateUrl: './delivery-detail.component.html',
    styleUrls: ['./delivery-detail.component.css'],
    standalone: false
})
export class DeliveryDetailComponent implements OnInit, OnDestroy {

  /**
   * Determines the view which can be: 0 = The current unsubmitted delivery, 1 = A completed delivery, 2 = A completed delivery being edited
   */
  viewMode: DeliveryMode = DeliveryMode.Current;
  
  /**
   * If viewing a completed delivery, index into {@link LocalStorageService.received_landings}
   */
  completedInd: number = 0;

  /**
   * When viewing a completed delivery, it can be edited and resubmitted within 7 days of its original submission date
   */
  canEdit: boolean = false;

  /**
   * The current landing/delivery
   */
  landing: GroundfishDelivery = new GroundfishDelivery();
  fishTicketColumns: string[] = ['Buyer', 'Fish Ticket Number', 'Date'];
  @ViewChild(MatTable) table!: MatTable<FishTicket>;

  /**
 * The current active trip
 */
  activeTrip: Trip = new Trip();

  previousSetIndex: number = 0;
  matToolTipText: string = "Back to Home"

  /**
   * Use a separate control for permit number
   */
  permitNumControl = new FormControl('', [Validators.required]);
  landingForm: FormGroup = new FormGroup({
    vessel: new FormControl(new VesselData('', ''), [Validators.required, vesselDataValidator()]), //Default to last vessel entered
    operator: new FormControl('',  [Validators.required]), //Default to account name
    deliverDate: new FormControl( [Validators.required]), //Default to today's date
    landingPort: new FormControl(new LookupItem(''), [Validators.required, lookupItemValidator()]), //Default to last port entered
    permitType: new FormControl(new LookupItem(''), [Validators.required, lookupItemValidator()]), //Default to last one entered
    isExemptedFP: new FormControl(false), //Default to last
    isEMTrip: new FormControl(false), //Default to last
    isObservedTrip: new FormControl(false) //Same here supposedly
  });

  /**
   * For populating the vessel dropdown
   */
  vesselOptions: VesselData[] = LocalStorageService.current_user.operatedVessels;

  /**
   * On every change to the landing form, this subscription catches it and runs the form update procedure
   */
  landingFormSubscription: Subscription = new Subscription();

  /**
   * For when the permit number dropdown value changes
   */
  permitNumSubscription: Subscription = new Subscription();
  /**
   * For populating the permit number dropdown
   */
  permitNums!: string[];

  portOptions: LookupItem[] = [];
  filteredPorts!: Observable<LookupItem[]>;
  portSubscription!: Subscription;

  permitTypeOptions: LookupItem[] = [];
  permitSubscription!: Subscription;

  lastBuyer: string = '';

  /**
   * Periodically updated from {@link TripValidator.errorWarningDialogContent}
   */
  errors: SetErrorBlock[] = [];
  onlyWarnings: boolean = false;
  /**
   * Which attached sets have errors?
   */
  validSets: Map<string, boolean> = new Map<string, boolean>();

  /**
   * When editing a completed delivery, we use this to keep track of which sets were originally submitted with the delivery
   */
  preExistingSets: Map<string, boolean> = new Map<string, boolean>();

  /**
   * The sets on this delivery, sorted
   */
  sortedSets: FishingEvent[] = [];

  landingSubscription!: Subscription;

  syncInProgress: boolean = LocalStorageService.sync_in_progress;
  syncProgSub!: Subscription;

  previousScreen: string = 'menu';

  constructor(
    public router: Router,
    private route: ActivatedRoute,
    private onlineStatusService: OnlineStatusService,
    private toastr: ToastrService, 
    private insights: Insights,
    public dialog: MatDialog, 
    private lookupService: LookupDataService,
    private common: CommonFunctionService) { 
    
  }

  /**
   * Initialization -- Are we viewing a completed delivery or the current unsubmitted one?
   * Populate the form fields if applicable using {@link LocalStorageService.pending_landing}.
   * If this is a new delivery we fill in some defaults. Set the dropdowns' values and set the form update.
   * All interactivity is disabled if viewing a completed delivery.
   */
  ngOnInit(): void {
    this.previousScreen = this.route.snapshot.queryParams.previous ?? 'menu';
    this.previousSetIndex = +(this.route.snapshot.queryParams.setIndex ?? 0);
    this.activeTrip = LocalStorageService.user_trip;
    this.insights.trackEvent('Entering delivery', `Initializing delivery details. Open sets ${this.activeTrip.sets.length}. Attached sets: ${this.activeTrip.groundfishDelivery.sets.length}`);

    LocalStorageService.sync_in_progress$.subscribe(val => this.syncInProgress = val);
    LocalStorageService.storage_promise.then(_ => {
      //Completed delivery or not, passed via URL
      this.viewMode = +this.route.snapshot.paramMap.get('pending')!;

      //Set the right tooltip depending on where the back button will take user
      if (this.previousScreen == 'set_detail') {
        this.matToolTipText = 'Back to Set';
      } else if (this.viewMode != 0) {
        this.matToolTipText = 'Back to Deliveries'
      }
      this.completedInd = this.viewMode != DeliveryMode.Current ? Number.parseInt(<string>this.route.snapshot.paramMap.get('index')) : -1;
      switch (this.viewMode) {
        case DeliveryMode.Current:
          this.landing = this.activeTrip.groundfishDelivery;
          break;
        case DeliveryMode.Completed:
          this.landing = LocalStorageService.received_logbooks[this.completedInd] as GroundfishDelivery;
          this.canEdit = new Date(this.landing.receivedDate.valueOf() + 7*24*60*60*1000) > new Date();
          break;
        case DeliveryMode.EditCompleted:
          this.landing = LocalStorageService.edited_logbook.GF;  
          if (new Date(this.landing.receivedDate.valueOf() + 7*24*60*60*1000) < new Date()) {
            this.landing = LocalStorageService.received_logbooks[this.completedInd] as GroundfishDelivery;
            this.discardEdits(false);
            this.toastr.warning('One week edit time window for this delivery has passed. Contact FedElog@psmfc.org if you have any questions', 'Logbook edits removed', {timeOut: 8000});            
          } else this.canEdit = true;
          break;
      }
      this.vesselOptions = this.viewMode == DeliveryMode.Completed ? VesselData.transformAnonymous([this.landing.vessel]) : LocalStorageService.current_user.operatedVessels;
      if (this.viewMode == DeliveryMode.Completed) {
        this.landingForm.disable();
        this.permitNumControl.disable();
      }

      if (this.landing.isNewDelivery && this.viewMode == DeliveryMode.Current) {
        this.insights.trackEvent('New delivery', 'Initializing new delivery');
        let last = LocalStorageService.received_logbooks.find(lb => lb instanceof GroundfishDelivery) as GroundfishDelivery;
        if (last) {
          this.landing.vessel = VesselData.transformAnonymous([last.vessel])[0];
          this.landing.returnPort = last.returnPort;
          if (last.tickets.length > 0) this.lastBuyer = last.tickets[last.tickets.length - 1].buyer;
        }

        this.landing.sets = this.activeTrip.sets.filter(st => st.fishery == 'Groundfish')
        .filter(fe => !isNaN(new Date(fe.endDate).getTime()) && fe.endLat && fe.endLon);

        this.landing.isNewDelivery = false;
        LocalStorageService.updateLanding(this.landing).then(result => {
          if (result.success) console.log("New delivery updated"); 
          else {
            this.toastr.error('Error updating delivery', 'Error', {timeOut: 3000});
            this.insights.trackException(result.error);
          }
        });

        this.landing.operatorName = LocalStorageService.current_user.firstName + " " + LocalStorageService.current_user.lastName;
      }

      
      this.permitNums = LocalStorageService.permit_nums;

      this.patchForm();
      
      if (this.viewMode != DeliveryMode.Completed) {
        if (this.viewMode == DeliveryMode.Current) {
          this.landingSubscription = LocalStorageService.user_trip$.subscribe(val => {
              this.landing = val.groundfishDelivery;
              console.log(this.landing)
              this.patchForm(false);
              this.sortSetsByDate();
              this.table.renderRows();
          });
        } else if (this.viewMode == DeliveryMode.EditCompleted) {
          this.landingSubscription = LocalStorageService.edited_logbook$.subscribe(val => {
              this.landing = val.GF;
              this.patchForm(false);
              this.sortSetsByDate();
              this.table.renderRows();
          });
        }

        this.subscribeToFormUpdate();
        this.resetErrorContent(); //Issue's here
        this.filteredPorts = this.landingForm.get('landingPort')!.valueChanges.pipe(
          startWith(''),
          map(value => this.portOptions.filter(option => option.name.toLowerCase().includes(value.toLowerCase())))
        );
      }

      this.portSubscription = this.lookupService.getPortsObservable().subscribe((value) => this.portOptions = value);     
      this.permitSubscription = this.lookupService.getPermitTypeObservable().subscribe((value) => this.permitTypeOptions = value);
      // sort landing sets
      this.sortSetsByDate();  
    });  
  }


  patchForm(emitEvent: boolean = true) {
    this.landingForm.patchValue({
      vessel: this.landing.vessel,
      operator: this.landing.operatorName,
      deliverDate: this.landing.returnDate ? formatDate(this.landing.returnDate, 'fullDate', 'en-US') : '',
      landingPort: this.landing.returnPort.name ? this.landing.returnPort : '',
      permitType: this.landing.permitType,
      isExemptedFP: this.landing.isEFP,
      isEMTrip: this.landing.isEMTrip,
      isObservedTrip: this.landing.isObservedTrip
    }, {emitEvent: emitEvent});
    this.permitNumControl.setValue(this.landing.permitNumber, {emitEvent: emitEvent});
  }

  /**
   * Sort the sets by date first then by their set numbers.
   */
  sortSetsByDate(): void {

    var compareSetNumbers = (a: string, b: string) => { return !b ? -1 : !a ? 1 : a.localeCompare(b); }
    var compareSetDates = (aString: string, bString: string, aNumber: string, bNumber: string) => {
      return (aString < bString) ? -1 : (aString > bString) ? 1 : compareSetNumbers(aNumber, bNumber); }

    this.landing.sets.sort((a, b) => {

      // start sorting, check if dates are null first
      return (a.startDate && !b.startDate) ? -1 : 
      (!a.startDate && b.startDate) ? 1 : 
      (!a.startDate && !b.startDate) ? compareSetNumbers(a.setNumber, b.setNumber) : 
      compareSetDates(new Date(a.startDate).toISOString(), new Date(b.startDate).toISOString(), a.setNumber, b.setNumber); 
    });

    this.sortedSets = this.landing.sets;
  }

/**
 * We update the landing in the database and the current landing object on every form update. 
 */
  subscribeToFormUpdate() {
    this.landingFormSubscription = this.landingForm.valueChanges.subscribe(value => {
      if (!value.vessel) {
        value.vessel = this.landing.vessel;
        this.landingForm.get('vessel')!.setValue(value.vessel, {onlySelf: true, emitEvent: false});
      }
      (this.landing as GroundfishDelivery).updateValue(value);
      LocalStorageService.updateLanding(this.landing).then(result => {
        if (result.success) {
          if (value.permitType == 'Open Access' && (this.permitNumControl.value as string).length != 6) this.permitNumControl.reset('');
          else if (this.permitNumControl.disabled && value.permitType != 'Open Access') this.permitNumControl.enable();
          
          if (value.permitType != 'IFQ') this.landingForm.get('isEMTrip')?.setValue(false, { emitEvent: false });
          this.resetErrorContent();
        } else {
          this.toastr.error('Error updating delivery', 'Error', {timeOut: 3000});
          this.insights.trackException(result.error);
        }      
      })      
    });

    //Separate subscription for permit number since it may be enabled/disabled 
    this.permitNumSubscription = this.permitNumControl.valueChanges.subscribe(value => {
      if (this.permitNumControl.enabled) {
        this.landing.permitNumber = value ? value : '';
        LocalStorageService.updateLanding(this.landing).then(result => {
          if (result.success) this.resetErrorContent();
          else {
            this.toastr.error('Error updating delivery', 'Error', {timeOut: 3000});
            this.insights.trackException(result.error);
          }
        });
      }
    });
  }


  compareFn(l1: LookupItem, l2: LookupItem): boolean {
    return l1 && l2 ? l1.code === l2.code : l1 === l2;
  }

  compareVessel(v1: VesselData, v2: VesselData): boolean {
    return v1 && v2 ? v1.vesselName+v1.vesselCoastNumber === v2.vesselName+v2.vesselCoastNumber : v1 === v2;
  }

  /**
   * Go get all errors/warnings for the delivery and its sets
   */
  resetErrorContent() {
    this.errors = TripValidator.errorWarningDialogContentGroundfish(this.viewMode == DeliveryMode.EditCompleted);
    this.onlyWarnings = !this.errors.some(x => x.isError) == true;
    this.validSets.clear();
    this.preExistingSets.clear();
    let allSets = LocalStorageService.user_trip.sets
    this.landing.sets.forEach(set => {
      this.validSets.set(set.fishingEventID, this.errors.find(x => x.setID == set.fishingEventID && x.isError) == undefined ? true : false);
      this.preExistingSets.set(set.fishingEventID, allSets.findIndex((fe: FishingEvent) => fe.fishingEventID == set.fishingEventID) == -1);
    });
  }


  /**
   * As in settings we can add a new vessel here.
   */
  addVessel() {
    this.common.addVessel(-1, false).then((newVessel: VesselData) => {
      console.log(newVessel)
      this.vesselOptions = LocalStorageService.current_user.operatedVessels;
      this.landingForm.get('vessel')?.setValue(newVessel);
    }).catch((err: any) => console.log(err))
  }


  /**
   * Display errors/warnings to the user if there are any. Otherwise we go to the summary-review component.
   */
  openSubmitDialog() {   
    if (this.onlineStatusService.getStatus() == OnlineStatusType.OFFLINE) {
      this.dialog.open(SubmitDialogComponent, {
        autoFocus: false,
        width: '400px',
        height: '450px',
        data: this.errors.filter(x => x.isError).length > 0
      });

    } else {
      this.openErrorDialog(true);
    }    
  }


  editDateTime() {
    if (this.viewMode == DeliveryMode.Completed) return;
    let dateInput = this.landingForm.get('deliverDate')?.value == '' ? new Date() : new Date(this.landingForm.get('deliverDate')?.value);

    const dialogRef = this.dialog.open(DateTimePickerComponent, {
      width: '500px',
      data: {date: dateInput, isDelivery: true, isEnd: true},
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) this.landingForm.get('deliverDate')?.setValue(formatDate(result, 'fullDate', 'en-US'));
    });
  }


  /**
   * Adding ar editing a fishing ticket
   * @param index Index into landing tickets array, if we are editing an existing ticket
   * @returns 
   */
  openFishTicketDialog(index: number) {
    if (this.viewMode == DeliveryMode.Completed) return;
    if (index >= this.landing.tickets.length) {
      this.landing.tickets.push(new FishTicket('', ''));
      this.landing.tickets[index].deliveryDate = this.landingForm.get('deliverDate')?.value == '' ? new Date() : new Date(this.landingForm.get('deliverDate')?.value);
      this.landing.tickets[index].buyer = this.lastBuyer;
    }

    let usedNumbers: string[] = [];
    this.landing.tickets.forEach(tck => {if (this.landing.tickets[index].ticketNumber != tck.ticketNumber) usedNumbers.push(tck.ticketNumber)});

    const dialogRef = this.dialog.open(AddTicketDialogComponent, {
      autoFocus: false,
      width: '400px',
      position: {top: '15px'},
      disableClose: true,
      data: {tckt: this.landing.tickets[index], usedNums: usedNumbers}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.delete) {
          this.landing.tickets.splice(index, 1);
        } else {
          this.landing.tickets[index].buyer = result.buyer;
          this.landing.tickets[index].ticketNumber = result.number;
          this.landing.tickets[index].deliveryDate = result.date;
        }
        this.table.renderRows();
        LocalStorageService.updateLanding(this.landing).then(result => {
          if (!result.success) {
            this.toastr.error('Error updating delivery', 'Error', {timeOut: 3000});
            this.insights.trackException(result.error);
          }
        });
      } else {
        this.landing.tickets.splice(index, 1);
      }
      this.resetErrorContent();
    });
  }


  /**
   * Adding a new permit number
   * @returns 
   */
  openPermitDialog() {
    if (this.viewMode == DeliveryMode.Completed) return;

    // Retrieve the current permit type from the landingForm.
    const permitType = this.landingForm.get('permitType')?.value?.name;

    const dialogRef = this.dialog.open(PermitNumDialogComponent, {
      width: '300px',
      // Pass along the permit type to the dialog.
      data: { permitType }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        LocalStorageService.addAuxiliaryData(result, 'permit_numbers').then(res => {
          if (res.success) { 
            this.permitNums = LocalStorageService.permit_nums;
            this.landing.permitNumber = result;
            this.permitNumControl.setValue(result);
          } else {
            this.toastr.error('Permit number already exists', 'Error', {timeOut: 3000});
            this.insights.trackException(res.error);
          }
          
        });
      } else {
        this.permitNumControl.setValue(this.landing.permitNumber);
      }
    });
  }

  get filteredPermitNums(): string[] {
    const permitType = this.landingForm.get('permitType')?.value?.name;
    if (permitType === 'Open Access') {
      // Only return permit numbers with 6 digits for Open Access
      return LocalStorageService.permit_nums.filter(num => num.length === 6);
    } else {
      // For IFQ and LE, only return permit numbers that are 4 digits
      return LocalStorageService.permit_nums.filter(num => num.length === 4);
    }
  }




  /**
   * For adding/removing sets attached to the delivery
   * @returns 
   */
  openSetSelection() {
    //Editing?
    if (this.viewMode == DeliveryMode.Completed) return;
    let ids: string[] = this.landing.sets.map(s => s.fishingEventID);
    console.log(this.activeTrip)
    const dialogRef = this.dialog.open(SetSelectionDialogComponent, {
      width: '400px',
      height: '600px',
      data: {
        data: ids,
        editedDelivery: this.viewMode == DeliveryMode.EditCompleted,
        fishery: 'Groundfish'
      }
    });

    dialogRef.afterClosed().subscribe((result: FishingEvent[]) => {
      if (result) {
        this.landing.sets = this.viewMode == DeliveryMode.EditCompleted ? 
                  this.landing.sets.filter((fe: FishingEvent) => this.preExistingSets.get(fe.fishingEventID)).concat(result) : result;
        LocalStorageService.updateLanding(this.landing).then(res => {
          if (!res.success) {
            this.toastr.error('Error updating delivery', 'Error', {timeOut: 3000});
            this.insights.trackException(res.error);
          } else {
            this.resetErrorContent();
            this.sortSetsByDate();
          }
        });       
      }
    });
  }


  /**
   * Clicking on a set attached to delivery takes us to that set
   * @param setID fishingEventID of set to go to
   * @returns 
   */
  goToSet(setID: string) {
    //Editing?
    if (this.viewMode == DeliveryMode.Completed) {
      // Find the set index from the landing sets.
      const setIndex = this.landing.sets.findIndex(x => x.fishingEventID === setID);
      if (setIndex !== -1) {
        // Navigate to the set page and pass a parameter that indicates "readOnly" mode.
        this.router.navigate(['set_detail', setIndex, false], { queryParams: { previous: 'deliveries_list', completedIndex: this.completedInd, readOnly: true } });
        if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) {
          window.scrollTo(0, 0);
        }
      }
      return;
    }
    let setIndex = this.activeTrip.sets.findIndex(x => x.fishingEventID === setID);
    if (setIndex != -1) {
      this.router.navigate(['set_detail', setIndex, false], { queryParams: { previous: 'delivery'} });
      if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) window.scrollTo(0, 0);
    } else if (this.viewMode == DeliveryMode.EditCompleted) {
      //The set no longer exists on device and is saved as part of this delivery rather than the user sets
      this.router.navigate(['set_detail', -1, false], { queryParams: { editedIndex: this.landing.sets.findIndex(x => x.fishingEventID === setID) } });
      if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) window.scrollTo(0, 0);
    }   

  }


  removeSet(index: number) {
    //Editing?
    this.landing.sets.splice(index, 1);
    LocalStorageService.updateLanding(this.landing).then(res => {
      if (!res.success) {
        this.toastr.error('Error updating delivery', 'Error', {timeOut: 3000});
        this.insights.trackException(res.error);
      }
      this.resetErrorContent()
    });
  }


  /**
   * Get the errors/warnings from {@link TripValidator.errorWarningDialogContent} and put it in the opened dialog
   */
  openErrorDialog(submission: boolean = false) {
    this.resetErrorContent();

    if (this.errors.length == 0 && submission) {
      console.log('Going to summary review')
      this.router.navigate(['summary_review', 'Groundfish'], { queryParams: { previous: this.previousScreen, deliveryIndex: this.viewMode == DeliveryMode.EditCompleted ? this.completedInd : -1 } });
      return;
    }

    //Launch dialog with collected results
    const dialogRef = this.dialog.open(ErrorWarningDialogComponent, {
      autoFocus: false,
      width:  (Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0) - 10).toString() + 'px',
      height: (Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0) - 10).toString() + 'px',
      data: {errors: this.errors, submission: submission}
    });
    
    dialogRef.afterClosed().subscribe(result => {
      if (result != undefined) {
        if (submission && result == -2) {
          //Go to summary review
          console.log('Going to summary review')
          this.router.navigate(['summary_review', 'Groundfish'], { queryParams: { previous: this.previousScreen, deliveryIndex: this.viewMode == DeliveryMode.EditCompleted ? this.completedInd : -1} });
        } else if (result != -1) {
          if (this.viewMode == DeliveryMode.EditCompleted)
            this.router.navigate(['set_detail', -1, false], { queryParams: { editedIndex: result } });
          else
            this.router.navigate(['set_detail', result, false], { queryParams: { previous: 'delivery' } });
          
          if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) window.scrollTo(0, 0);
        }
      }
    });
  }


  enableEdits() {
    //Check for edits in progress on another delivery
    let logbook = LocalStorageService.edited_logbook.GF.logbookId ? LocalStorageService.edited_logbook.GF : LocalStorageService.edited_logbook.HMS;
    if (logbook.logbookId && logbook.logbookId != this.landing.logbookId) {
      this.toastr.warning(`Logbook ${logbook.logbookId.substring(17)} has unsubmitted edits. Submit or discard those to edit this logbook`, 
      'Multiple edited logbooks');
      return;
    }

    LocalStorageService.editExistingGroundfishLanding(this.landing.logbookId).then(res => {
      if (!res.success) {
        this.toastr.error('Error editing delivery', 'Error', {timeOut: 3000});
        this.insights.trackException(res.error);
        return;
      }

      this.viewMode = DeliveryMode.EditCompleted;
      this.router.navigate(['delivery_detail', 2, this.completedInd], { queryParams: { previous: 'deliveries_list' } });
      this.landing = LocalStorageService.edited_logbook.GF;
      
      this.landingForm.enable();
      if (this.landing.permitType.name != 'Open Access') this.permitNumControl.enable();

      this.subscribeToFormUpdate();
      this.resetErrorContent();
      this.filteredPorts = this.landingForm.get('landingPort')!.valueChanges.pipe(
        startWith(''),
        map(value => this.portOptions.filter(option => option.name.toLowerCase().includes(value.toLowerCase())))
      );

      this.landingSubscription = LocalStorageService.edited_logbook$.subscribe(val => {
        this.landing = val.GF;
        this.patchForm();
        this.sortSetsByDate();
        this.table.renderRows();
      });

      this.vesselOptions = LocalStorageService.current_user.operatedVessels;
    })
    
  }


  discardEdits(promptUser: boolean) {
    //Lots of warnings and user confirmation before going forward
    let discard = () => {
      this.viewMode = DeliveryMode.Completed;

      this.landingSubscription?.unsubscribe(); 
      this.landingFormSubscription?.unsubscribe();
      this.permitSubscription?.unsubscribe();
      this.landingForm.disable();
      this.permitNumControl.disable();

      LocalStorageService.discardLogbookEdits();

      //Unassign LocalStorageService.edited_landing, revert this landing to completed delivery landing
      this.landing = LocalStorageService.received_logbooks[this.completedInd] as GroundfishDelivery;
      this.vesselOptions = VesselData.transformAnonymous([LocalStorageService.received_logbooks[this.completedInd].vessel]);
      this.landingForm.patchValue({
        vessel: LocalStorageService.received_logbooks[this.completedInd].vessel,
        operator: LocalStorageService.received_logbooks[this.completedInd].operatorName,
        deliverDate: this.landing.returnDate ? formatDate(this.landing.returnDate, 'fullDate', 'en-US') : '',
        landingPort: this.landing.returnPort.name ? this.landing.returnPort : '',
        permitType: this.landing.permitType,
        isExemptedFP: this.landing.isEFP,
        isEMTrip: this.landing.isEMTrip,
        isObservedTrip: this.landing.isObservedTrip
      });
      this.permitNumControl.setValue(this.landing.permitNumber);
      if (this.landing.permitType.name === 'Open Access') this.permitNumControl.disable();
      this.sortSetsByDate();
    };

    if (promptUser) {
      this.dialog.open(ConfirmDialogComponent, {
        data: {screen: 'discardedits'}
      }).afterClosed().subscribe(result => {
        if (!result) return;
        discard();  
      });  
    } else discard();
    
  }


  navigateBack() {
    if (this.previousScreen == 'set_detail') this.router.navigate(['set_detail', this.previousSetIndex, false]);
    else this.router.navigate([this.previousScreen]);
  }


  ngOnDestroy() {
    if (this.viewMode != DeliveryMode.Completed) {
      if (this.landingFormSubscription) this.landingFormSubscription.unsubscribe();
      if (this.portSubscription) this.portSubscription.unsubscribe();
      if (this.permitSubscription) this.permitSubscription.unsubscribe();
      if (this.landingSubscription) this.landingSubscription.unsubscribe(); 
      if (this.syncProgSub) this.syncProgSub.unsubscribe();
    }   
  }
}
